/**
 * Composant Hourly Chart
 * Affiche l'activité par heure avec identification des heures de pointe
 */

import {
  Component,
  OnInit,
  input,
  inject,
  ElementRef,
  ViewChild,
  AfterViewInit,
  DestroyRef,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AnalyticsService,
  HourlyActivity,
  HourlyData,
} from '../../../../services/analytics.service';
import { LogService } from '../../../../../services/log.service';

@Component({
  selector: 'app-hourly-chart',
  imports: [CommonModule],
  templateUrl: './hourly-chart.component.html',
  styleUrls: ['./hourly-chart.component.css'],
})
export class HourlyChartComponent implements OnInit, AfterViewInit {
  private log = inject(LogService);
  private analyticsService = inject(AnalyticsService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  readonly date = input<Date>(new Date());
  @ViewChild('chartContainer') chartContainer!: ElementRef;

  hourlyData: HourlyData[] = [];
  peakHours: number[] = [];
  summary: HourlyActivity['summary'] | null = null;
  isLoading = true;
  error: string | null = null;
  maxRevenue = 0;

  ngOnInit(): void {
    this.loadHourlyData();
  }

  ngAfterViewInit(): void {
    // Chart rendering after view init if needed
  }

  loadHourlyData(): void {
    this.isLoading = true;
    this.error = null;

    this.analyticsService
      .getHourlyActivity(this.date())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.hourlyData = data.hourly;
          this.peakHours = data.peakHours;
          this.summary = data.summary;
          this.maxRevenue = Math.max(...data.hourly.map((h) => h.revenue), 1);
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.log.error('Erreur chargement activité horaire:', err);
          this.error = 'Impossible de charger les données';
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  getBarHeight(revenue: number): string {
    const percentage = (revenue / this.maxRevenue) * 100;
    return `${Math.max(percentage, 5)}%`; // Min 5% pour visibilité
  }

  getBarColor(hour: number, isPeak: boolean): string {
    if (isPeak) {
      return 'bg-gradient-to-t from-indigo-600 to-purple-500';
    }
    return 'bg-indigo-300';
  }

  formatHour(hour: number): string {
    return `${hour}h`;
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }

  getPeakHoursText(): string {
    if (this.peakHours.length === 0) return 'Aucune';
    return this.peakHours.map((h) => `${h}h`).join(', ');
  }
}
